import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import type { RootState } from "../../app/store";
import { setTbsTenant } from "../../services/tbsTenant/tbsTenantSlice";
import { tbsContactsApi } from "../../services/tbsContacts/tbsContacts";
import { tbsItemsApi } from "../../services/tbsItems/tbsItems";
import { tbsConfigApi } from "../../services/tbsConfig/tbsConfig";
import { tbsInvoicesApi } from "../../services/tbsInvoices/tbsInvoices";
import { tbsPaymentsApi } from "../../services/tbsPayments/tbsPayments";
import { tbsShiftsApi } from "../../services/tbsShifts/tbsShifts";
import { tbsReportsApi } from "../../services/tbsReports/tbsReports";
import {
  readMemberships,
  resolveTenantId,
  loadStoredTenantId,
  storeTenantId,
  shouldOfferChoice,
  type TbsMembership,
} from "../../utils/tbsTenant";

// The company the management screens act for — APP-1 (`TBS-47`).
//
// Owns the choosing, the persisting and the cache reset, because those three have to happen together
// and no single component is the right place for all of them.

/** Every TBS slice. Listed rather than derived: a new one must be added here on purpose. */
const TBS_APIS = [
  tbsContactsApi,
  tbsItemsApi,
  tbsConfigApi,
  tbsInvoicesApi,
  tbsPaymentsApi,
  tbsShiftsApi,
  tbsReportsApi,
];

export interface UseTbsTenant {
  tenantId: string;
  memberships: TbsMembership[];
  offerChoice: boolean;
  selectTenant: (tenantId: string) => void;
}

export const useTbsTenant = (): UseTbsTenant => {
  const { user } = useAuth0();
  const dispatch = useDispatch();
  const stored = useSelector((state: RootState) => state.tbsTenant.tenantId);

  const memberships = useMemo(() => readMemberships(user), [user]);

  // **Resolved here rather than read from the store**, so the very first render already has the
  // right answer. Waiting on the effect below would give one paint where the company is unknown —
  // and with several memberships `TbsGuard` would render its refusal for that frame, which looks
  // exactly like being denied access.
  const tenantId = useMemo(
    () => (stored ? stored : resolveTenantId(memberships, loadStoredTenantId())),
    [memberships, stored],
  );

  // Push it into the store so `prepareHeaders` can see it. `prepareHeaders` is a module-level
  // closure and cannot use a hook, which is the whole reason the value lives in Redux.
  useEffect(() => {
    if (tenantId && tenantId !== stored) dispatch(setTbsTenant(tenantId));
  }, [tenantId, stored, dispatch]);

  // **A stored company the token no longer allows is cleared from storage too**, not merely ignored.
  // Left behind it would be re-read on the next load and dropped again on every visit, and the
  // symptom — the selector quietly resetting — is a long way from "access was revoked".
  useEffect(() => {
    const persisted = loadStoredTenantId();
    if (persisted && memberships.length > 0 && !memberships.some((m) => m.id === persisted)) {
      storeTenantId("");
    }
  }, [memberships]);

  const selectTenant = useCallback(
    (next: string) => {
      if (next === tenantId) return;
      storeTenantId(next);
      dispatch(setTbsTenant(next));

      // **Every TBS cache is thrown away, not invalidated.** Invalidation refetches by tag and would
      // leave the previous company's rows on screen until each request returned; showing one
      // company's products under another company's name for even a moment is worse than a spinner.
      // Cross-slice, so it belongs here rather than in any one slice — the same reasoning
      // `useAlegraSync` applies.
      TBS_APIS.forEach((api) => dispatch(api.util.resetApiState()));
    },
    [tenantId, dispatch],
  );

  return { tenantId, memberships, offerChoice: shouldOfferChoice(memberships), selectTenant };
};

export default useTbsTenant;
